import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  IoArrowBack,
  IoCloudUploadOutline,
  IoTrashOutline,
  IoAttach,
} from "react-icons/io5";
import { showMessageBox } from "../../../site/fn";
import "./Assets.css";

const AUTH_TOKEN_KEY = "zTaskAuthToken";
const ASSET_API_URL = `${process.env.REACT_APP_ASSET_SERVICE_BASE_URL}/api`;
const ATTACHMENT_API_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL;
const TOOLS_API_URL = `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/api`;

const getApiHeaders = (isMultipart = false) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (!token) throw new Error("Authentication token not found.");
  const headers = { Authorization: `Bearer ${token}` };
  if (!isMultipart) {
    headers["Content-Type"] = "application/json";
    headers["Accept"] = "application/json";
  }
  return headers;
};

const initialFormData = {
  asset_code: "",
  name: "",
  description: "",
  year_of_purchase: "",
  year_of_manufacture: "",
  registration_number: "",
  serial_number: "",
  asset_type: "",
  capacity_mt: "",
  purchase_cost: "",
  written_down_value: "",
  date_of_purchase: "",
  supplier: "",
  currency: "",
  import_configuration: "",
  is_sold: false,
  avg_km_per_liter: "",
  avg_hr_per_liter: "",
  image_url: "",
  active: true,
};

export default function AssetForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState(initialFormData);
  const [assetTypes, setAssetTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileDescriptions, setFileDescriptions] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const loadDropdowns = async () => {
      const load = async (column) => {
        const res = await fetch(
          `${TOOLS_API_URL}/get-column/common_master/${column}`,
          { headers: getApiHeaders() }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data)
          ? data.map((item) => ({ id: item.id, name: item.value }))
          : [];
      };
      try {
        const [types, curs] = await Promise.all([
          load("asset_type"),
          load("currency"),
        ]);
        setAssetTypes(types);
        setCurrencies(curs);
      } catch (e) {
        console.error(e);
      }
    };
    loadDropdowns();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    const loadAsset = async () => {
      setPageLoading(true);
      try {
        const res = await fetch(`${ASSET_API_URL}/assets/${id}`, {
          headers: getApiHeaders(),
        });
        if (!res.ok) throw new Error("Failed to load asset.");
        const asset = await res.json();
        setFormData({
          ...initialFormData,
          ...asset,
          date_of_purchase: asset.date_of_purchase
            ? String(asset.date_of_purchase).split("T")[0]
            : "",
          active: asset.active === undefined ? true : asset.active,
          is_sold: Boolean(asset.is_sold),
        });
        const attRes = await fetch(
          `${ATTACHMENT_API_URL}/attachment/api/attachments/${id}/Assets`,
          { headers: getApiHeaders() }
        );
        if (attRes.ok) {
          const attachments = await attRes.json();
          setExistingAttachments(
            (attachments || []).filter((att) => att.active)
          );
        }
      } catch (error) {
        showMessageBox(error.message || "Failed to load asset.", "error");
      } finally {
        setPageLoading(false);
      }
    };
    loadAsset();
  }, [id, isEdit]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "is_sold" || name === "active" ? String(val) === "true" : val,
    }));
  };

  const addFiles = (files) => {
    setSelectedFiles((prev) => [...prev, ...files]);
    setFileDescriptions((prev) => [...prev, ...Array(files.length).fill("")]);
  };

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const uploadFiles = async (assetId) => {
    if (selectedFiles.length === 0) return;
    const uploadFormData = new FormData();
    selectedFiles.forEach((file) => uploadFormData.append("files", file));
    fileDescriptions.forEach((desc) =>
      uploadFormData.append("descriptions[]", desc || "")
    );
    const response = await fetch(
      `${ATTACHMENT_API_URL}/attachment/api/attachments/${assetId}/Assets`,
      { method: "POST", headers: getApiHeaders(true), body: uploadFormData }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "File upload failed.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const payload = {
      ...formData,
      year_of_purchase: formData.year_of_purchase
        ? Number(formData.year_of_purchase)
        : null,
      year_of_manufacture: formData.year_of_manufacture
        ? Number(formData.year_of_manufacture)
        : null,
      capacity_mt: formData.capacity_mt ? Number(formData.capacity_mt) : null,
      purchase_cost: formData.purchase_cost
        ? Number(formData.purchase_cost)
        : null,
      written_down_value: formData.written_down_value
        ? Number(formData.written_down_value)
        : null,
      date_of_purchase: formData.date_of_purchase || null,
    };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "" || payload[key] === null) delete payload[key];
    });

    try {
      let assetId = id;
      if (isEdit) {
        const response = await fetch(`${ASSET_API_URL}/assets/${id}`, {
          method: "PUT",
          headers: getApiHeaders(),
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || errorData.message || "Failed to update asset."
          );
        }
      } else {
        const response = await fetch(`${ASSET_API_URL}/assets/`, {
          method: "POST",
          headers: getApiHeaders(),
          body: JSON.stringify(payload),
        });
        const responseData = await response.json();
        if (!response.ok || response.status !== 201) {
          throw new Error(
            responseData.error ||
              responseData.message ||
              "Failed to create asset."
          );
        }
        assetId = responseData.id;
      }
      if (selectedFiles.length > 0) await uploadFiles(assetId);
      showMessageBox(
        isEdit ? "Asset updated successfully." : "Asset created successfully.",
        "success"
      );
      navigate("/assets");
    } catch (error) {
      showMessageBox(error.message || "Save failed.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const getFileNameFromUrl = (url) => {
    try {
      return decodeURIComponent(String(url).split("/").pop());
    } catch {
      return url;
    }
  };

  if (pageLoading) {
    return (
      <div className="assets-container">
        <p>Loading asset...</p>
      </div>
    );
  }

  return (
    <div className="assets-container">
      <div className="assets-form-page-header">
        <button
          type="button"
          className="assets-btn assets-btn-secondary"
          onClick={() => navigate("/assets")}
        >
          <IoArrowBack size={18} />
          Back to Assets
        </button>
        <h1 className="assets-title">
          {isEdit ? "Edit Asset" : "Add New Asset"}
        </h1>
        <p className="assets-subtitle">
          {isEdit
            ? `Editing: ${formData.name || formData.asset_code || id}`
            : "Fill in the details below to add a new asset."}
        </p>
      </div>

      <div className="assets-form-container">
        <form onSubmit={handleSubmit} className="assets-form" noValidate>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="asset_code">
                Asset Code <span className="required-asterisk">*</span>
              </label>
              <input
                id="asset_code"
                name="asset_code"
                className="form-input"
                value={formData.asset_code}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="name">
                Asset Name <span className="required-asterisk">*</span>
              </label>
              <input
                id="name"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="asset_type">
                Asset Type <span className="required-asterisk">*</span>
              </label>
              <select
                id="asset_type"
                name="asset_type"
                className="form-select"
                value={formData.asset_type}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              >
                <option value="" disabled>
                  Select Type
                </option>
                {assetTypes.map((type) => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group grid-column-span-3">
              <label className="form-label" htmlFor="description">
                Description <span className="required-asterisk">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                className="form-textarea"
                rows="3"
                value={formData.description}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="registration_number">
                Registration Number <span className="required-asterisk">*</span>
              </label>
              <input
                id="registration_number"
                name="registration_number"
                className="form-input"
                value={formData.registration_number}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="serial_number">
                Serial Number <span className="required-asterisk">*</span>
              </label>
              <input
                id="serial_number"
                name="serial_number"
                className="form-input"
                value={formData.serial_number}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="year_of_manufacture">
                Year of Manufacture <span className="required-asterisk">*</span>
              </label>
              <input
                type="number"
                id="year_of_manufacture"
                name="year_of_manufacture"
                className="form-input"
                value={formData.year_of_manufacture}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="year_of_purchase">
                Year of Purchase <span className="required-asterisk">*</span>
              </label>
              <input
                type="number"
                id="year_of_purchase"
                name="year_of_purchase"
                className="form-input"
                value={formData.year_of_purchase}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="purchase_cost">
                Purchase Cost <span className="required-asterisk">*</span>
              </label>
              <input
                type="number"
                id="purchase_cost"
                name="purchase_cost"
                className="form-input"
                value={formData.purchase_cost}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="currency">
                Currency <span className="required-asterisk">*</span>
              </label>
              <select
                id="currency"
                name="currency"
                className="form-select"
                value={formData.currency}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              >
                <option value="" disabled>
                  Select
                </option>
                {currencies.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="date_of_purchase">
                Date of Purchase <span className="required-asterisk">*</span>
              </label>
              <input
                type="date"
                id="date_of_purchase"
                name="date_of_purchase"
                className="form-input"
                value={formData.date_of_purchase}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="written_down_value">
                Written Down Value (WDV)
              </label>
              <input
                type="number"
                id="written_down_value"
                name="written_down_value"
                className="form-input"
                value={formData.written_down_value}
                onChange={handleInputChange}
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="supplier">
                Supplier <span className="required-asterisk">*</span>
              </label>
              <input
                id="supplier"
                name="supplier"
                className="form-input"
                value={formData.supplier}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="capacity_mt">
                Capacity (MT) <span className="required-asterisk">*</span>
              </label>
              <input
                type="number"
                id="capacity_mt"
                name="capacity_mt"
                className="form-input"
                value={formData.capacity_mt}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="avg_km_per_liter">
                Avg KM/Liter <span className="required-asterisk">*</span>
              </label>
              <input
                id="avg_km_per_liter"
                name="avg_km_per_liter"
                className="form-input"
                value={formData.avg_km_per_liter}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="avg_hr_per_liter">
                Avg HR/Liter <span className="required-asterisk">*</span>
              </label>
              <input
                id="avg_hr_per_liter"
                name="avg_hr_per_liter"
                className="form-input"
                value={formData.avg_hr_per_liter}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="is_sold">
                Is Sold
              </label>
              <select
                id="is_sold"
                name="is_sold"
                className="form-select"
                value={String(formData.is_sold)}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="active">
                Active Status
              </label>
              <select
                id="active"
                name="active"
                className="form-select"
                value={String(formData.active)}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div className="form-group grid-column-span-3">
              {isEdit && (
                <div className="attachment-section">
                  <h3 className="attachment-section-title">
                    Existing Attachments
                  </h3>
                  {existingAttachments.length > 0 ? (
                    <ul className="existing-attachments-list">
                      {existingAttachments.map((att) => (
                        <li key={att.id}>
                          <a
                            href={att.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IoAttach /> {getFileNameFromUrl(att.file_url)}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-attachments-message">
                      No existing attachments.
                    </p>
                  )}
                </div>
              )}
              <div className="attachment-section">
                <h3 className="attachment-section-title">
                  {isEdit ? "Add New Attachments" : "Attachments"}
                </h3>
                <label
                  htmlFor="image_upload"
                  className={`drop-zone ${isDragging ? "drag-over" : ""}`}
                  onDrop={handleFileDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnter={() => setIsDragging(true)}
                  onDragLeave={() => setIsDragging(false)}
                >
                  <input
                    id="image_upload"
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    className="file-input-hidden"
                    disabled={isLoading}
                    onChange={(e) => {
                      if (e.target.files)
                        addFiles(Array.from(e.target.files));
                      e.target.value = null;
                    }}
                  />
                  <div className="drop-zone-content">
                    <IoCloudUploadOutline />
                    <span>
                      {isDragging
                        ? "Drop files here"
                        : "Drag & Drop or Click to Upload"}
                    </span>
                  </div>
                </label>
                {selectedFiles.length > 0 && (
                  <div className="file-description-list">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="file-description-item"
                      >
                        <span className="file-name-label">{file.name}</span>
                        <input
                          type="text"
                          className="file-description-input"
                          value={fileDescriptions[index]}
                          disabled={isLoading}
                          placeholder="Description..."
                          onChange={(e) => {
                            const next = [...fileDescriptions];
                            next[index] = e.target.value;
                            setFileDescriptions(next);
                          }}
                        />
                        <button
                          type="button"
                          className="remove-file-btn"
                          disabled={isLoading}
                          onClick={() => {
                            setSelectedFiles((prev) =>
                              prev.filter((_, i) => i !== index)
                            );
                            setFileDescriptions((prev) =>
                              prev.filter((_, i) => i !== index)
                            );
                          }}
                        >
                          <IoTrashOutline />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="assets-btn assets-btn-secondary"
              disabled={isLoading}
              onClick={() => navigate("/assets")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="assets-btn assets-btn-primary"
              disabled={isLoading}
            >
              {isLoading
                ? "Saving..."
                : isEdit
                  ? "Update Asset"
                  : "Create Asset"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
